import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { capturePrivateAccessToken } from "@/lib/privateAccessToken";

export type CallbackRequestErrorCode =
  | "no_outbound_binding"
  | "rate_limited"
  | "outbound_failed"
  | "unknown";

export interface CallbackRequestResult {
  ok: boolean;
  errorCode?: CallbackRequestErrorCode;
  errorMessage?: string;
}

export type EmailQuestionErrorCode = "no_mailbox" | "rate_limited" | "unknown";

export interface EmailQuestionResult {
  ok: boolean;
  errorCode?: EmailQuestionErrorCode;
  errorMessage?: string;
}

interface SupabaseFunctionError {
  message?: string;
  context?: {
    status?: number;
    json?: () => Promise<{ error?: string; message?: string }>;
  };
}

/** Best-effort read of the edge function's error body — falls back gracefully
 *  when the function doesn't exist yet (e.g. a 404 with no JSON body) or the
 *  response isn't JSON at all. Never throws. */
const readErrorBody = async (
  error: SupabaseFunctionError,
): Promise<{ status?: number; code?: string; message?: string }> => {
  const status = error.context?.status;
  try {
    if (error.context && typeof error.context.json === "function") {
      const body = await error.context.json();
      return { status, code: body?.error, message: body?.message || error.message };
    }
  } catch {
    /* non-JSON body (e.g. a bare 404) — fall through to the status-only read */
  }
  return { status, message: error.message };
};

interface UseChannelRequestsArgs {
  agentId?: string | null;
  urlName?: string | null;
}

/**
 * Wraps the two visitor-facing channel edge functions (`request-callback`,
 * `email-question`) with typed results. Whether a callback is offered at all
 * comes from the Genie itself (`agent.outbound_connected`), not from here.
 * Every call is defensive — callers always resolve to a calm
 * `{ ok: false, errorCode }` rather than throwing.
 */
export const useChannelRequests = ({ agentId, urlName }: UseChannelRequestsArgs) => {
  const requestCallback = useCallback(
    async (args: { phoneE164: string; visitorName?: string; pageUrl: string }): Promise<CallbackRequestResult> => {
      if (!agentId) return { ok: false, errorCode: "unknown", errorMessage: "This Genie isn't ready yet." };
      try {
        // Private Genies opened via an access link prove access the same way
        // the page load did.
        const accessToken = capturePrivateAccessToken();
        const { data, error } = await supabase.functions.invoke("request-callback", {
          ...(accessToken && { headers: { "x-agent-access-token": accessToken } }),
          body: {
            action: "request",
            agent_id: agentId,
            url_name: urlName,
            phone_e164: args.phoneE164,
            visitor_name: args.visitorName,
            consent: true,
            page_url: args.pageUrl,
          },
        });
        if (error) {
          const { status, code, message } = await readErrorBody(error as SupabaseFunctionError);
          if (status === 409 || code === "no_outbound_binding") {
            return { ok: false, errorCode: "no_outbound_binding", errorMessage: message };
          }
          if (status === 429 || code === "rate_limited") {
            return { ok: false, errorCode: "rate_limited", errorMessage: message };
          }
          if (status === 502 || code === "outbound_failed") {
            return { ok: false, errorCode: "outbound_failed", errorMessage: message };
          }
          return { ok: false, errorCode: "unknown", errorMessage: message };
        }
        if (data?.error) {
          return { ok: false, errorCode: (data.error as CallbackRequestErrorCode) || "unknown", errorMessage: data.message };
        }
        return { ok: true };
      } catch (e) {
        return { ok: false, errorCode: "unknown", errorMessage: e instanceof Error ? e.message : "Request failed" };
      }
    },
    [agentId, urlName],
  );

  const askQuestion = useCallback(
    async (args: {
      visitorEmail: string;
      visitorName?: string;
      question: string;
      pageUrl: string;
    }): Promise<EmailQuestionResult> => {
      if (!agentId) return { ok: false, errorCode: "unknown", errorMessage: "This Genie isn't ready yet." };
      try {
        const { data, error } = await supabase.functions.invoke("email-question", {
          body: {
            agent_id: agentId,
            url_name: urlName,
            visitor_email: args.visitorEmail,
            visitor_name: args.visitorName,
            question: args.question,
            page_url: args.pageUrl,
          },
        });
        if (error) {
          const { status, code, message } = await readErrorBody(error as SupabaseFunctionError);
          if (status === 409 || code === "no_mailbox") {
            return { ok: false, errorCode: "no_mailbox", errorMessage: message };
          }
          if (status === 429 || code === "rate_limited") {
            return { ok: false, errorCode: "rate_limited", errorMessage: message };
          }
          return { ok: false, errorCode: "unknown", errorMessage: message };
        }
        if (data?.error) {
          return { ok: false, errorCode: (data.error as EmailQuestionErrorCode) || "unknown", errorMessage: data.message };
        }
        return { ok: true };
      } catch (e) {
        return { ok: false, errorCode: "unknown", errorMessage: e instanceof Error ? e.message : "Request failed" };
      }
    },
    [agentId, urlName],
  );

  return { requestCallback, askQuestion };
};

export default useChannelRequests;
